import React from 'react';
import {
  OFFER_PRODUCT_TYPES,
  PageTemplateProps,
  DBSProduct,
  GetBankPropsFnParams,
  BankPageProps,
} from '../../types';
import {
  getLocaleBasePath,
  distributionChannels,
  getDistributionChannel,
  COUNTRY_CODE_CURRENCIES,
  isAppDuringBuildProcess,
  ENVIRONMENT_DOMAINS,
  getDomainConfig,
  INTL_LOCALES,
} from '@raisin/cp-utils';
import { ProductLabel } from '@raisin/components-lib';
import kebabCase from 'lodash/kebabCase';

import messages from '../../messages';
import { getInterestRateType } from 'utils/getInterestRateType';
import { formatTerm } from './bankOffers';
import bankDetailsMessages from '../../components/Banks/BankDetails/messages';
import { NextRouter } from 'next/router';
import Cache from '../cache';
import fetchBankDetails from 'api/dbs/bank';
import { fetchPublicProducts } from 'api/dbs';

export const LoggedInProductPageRoute = 'products';

export const SOURCE_TAX_TYPES = {
  domestic: 'DOMESTIC',
};

export const BANK_APIS_CACHE_TTL = 30 * 60 * 1000;

export const getBankSlug = (
  shouldUseBankSlugFallback: boolean,
  bankSlug: string,
  bankName: string,
) => (shouldUseBankSlugFallback ? `${kebabCase(bankName)}` : bankSlug);

/**
 * Returns cache keys for banks based on locale and bank id
 *
 * @param {string} locale Current page locale.
 * @param {string} bankId optional bank id, if not provided will return empty bankId. Example: dbs.banks?locale=fr&bankId=
 * @returns {Object} object with bank cache keys.
 */
export const getBankCacheKeys = (locale: string, bankId = '') => ({
  banks: `dbs.banks?locale=${locale}`,
  bankDetails: `dbs.banks?locale=${locale}&bankId=${bankId}`,
  bankProducts: `dbs.products?locale=${locale}`,
});

export const bankPageConfig: PageTemplateProps['bankPage'] = {
  isLoginRequired: false,
  bank_id: null,
  bank_details: null,
  bank_offers: [],
  bank_offer_id: null,
  highestInterestRate: '',
  titleId: 'bankPageTitle',
  navigation_settings: {
    hide_main_navigation: false,
    hide_footer: false,
    hide_footer_links: false,
  },
  seo_settings: {
    meta_title: '',
    meta_description: '',
    canonical_page: '',
    meta_robots: 'index, nofollow',
    opengraph: { title: '', description: '' },
  },
};

export const getBankOfferId = (params) => params?.page?.[2] ?? null;

export const getProductsPageLink = (locale?: string, isLoggedIn?: boolean) => {
  if (!locale) return '#';

  const localeBasePath = getLocaleBasePath(locale);
  const localeProductsPath =
    (isLoggedIn ? LoggedInProductPageRoute : getDomainConfig(locale).productPageRoutes) || '';
  return `${localeBasePath}/${localeProductsPath}/`;
};

export const getBankDetailPageLink = (
  locale: string,
  shouldUseBankSlugFallback: boolean,
  bankSlug: string,
  bankName: string,
) => {
  if (!locale) return '#';

  const bankSlugValue = getBankSlug(shouldUseBankSlugFallback, bankSlug, bankName);

  const localeBasePath = getLocaleBasePath(locale);
  const localeBankPagePath = getDomainConfig(locale).localisedBankPages || '';
  return `${localeBasePath}/${localeBankPagePath}/${bankSlugValue}`.replace(/\/?$/, '/');
};

export const getBankProductPageLink = (
  locale: string,
  bankName: string,
  bankId: string,
  productId: string,
) => {
  if (!locale) return '#';

  return `${getBankDetailPageLink(locale, false, bankName, bankName)}${productId}`;
};

export const buildCanonicalURL = (
  router: NextRouter,
  locale: string,
  environment = 'production',
) => {
  const siteUrl = getDomainConfig(locale).siteUrl || '';
  const envPrefix = ENVIRONMENT_DOMAINS[environment] || '';
  const basePath = siteUrl.replace('www.', `www.${envPrefix}`);

  let path = '';

  if (Array.isArray(router?.query?.page)) {
    path = `${path}${router.query.page.join('/')}/`;
  }

  return `${basePath}/${path}`;
};

export const prepareDepositTakingBank = (offer: DBSProduct) => ({
  name: offer.deposit_taking_bank.name,
  statutoryDepositGuaranteeScheme: {
    country: {
      name: offer.deposit_taking_bank.statutory_deposit_guarantee_scheme.country?.name
        ? offer.deposit_taking_bank.statutory_deposit_guarantee_scheme.country?.name
        : 'N/A',
    },
    insuredAmount: Number(
      offer.deposit_taking_bank.statutory_deposit_guarantee_scheme.insured_amount,
    ),
    currency: offer.deposit_taking_bank.statutory_deposit_guarantee_scheme.currency,
  },
});

export const calculateMinHeight = (querySelector: string, totalItems: number) => {
  const bankItem = document.querySelector<HTMLElement>(`.${querySelector}`);

  if (!bankItem) return 0;

  const bankItemHeight = bankItem ? bankItem.offsetHeight : 0;
  return totalItems * bankItemHeight;
};
/**
 * @see getBankOffersTableConfig
 */
const COMMON_BANK_CONFIGS_IN_DE_AT = {
  rate: { width: [4, 1.9] },
  duration: { width: [4, 2.7] },
  depositGuarantee: { width: [3, 1.6], visibility: ['none', 'block'] },
  product: { width: [4, 2.8] },
  orderBtn: { width: [12, 3] },
};

const BANK_OFFERS_TABLE_CONFIGS = {
  default: {
    rate: { width: [4, 1.8] },
    duration: { width: [4, 1.8] },
    depositGuarantee: { width: [3, 1.8], visibility: ['none', 'block'] },
    product: { width: [4, 4.2] },
    orderBtn: { width: [12, 2.4] },
  },
  [distributionChannels['en-GB']]: {
    rate: { width: [6, 1.5] },
    duration: { width: [6, 1.6] },
    depositGuarantee: { width: [6, 3.2], visibility: ['none', 'block'] },
    product: { width: [6, 2.1] },
    orderBtn: { width: [12, 2.4] },
  },
  [distributionChannels['nl-NL']]: {
    rate: { width: [6, 1.4] },
    duration: { width: [6, 1.9] },
    depositGuarantee: { width: [3, 0.6], visibility: ['none', 'block'] },
    product: { width: [12, 3.1] },
    orderBtn: { width: [12, 2.9] },
  },
  [distributionChannels['fr-FR']]: {
    rate: { width: [6, 2.4] },
    duration: { width: [6, 2.4] },
    depositGuarantee: { width: [3, 1.8], visibility: ['none', 'block'] },
    product: { width: [12, 2.8] },
    orderBtn: { width: [12, 2.4] },
  },
  [distributionChannels['es-ES']]: {
    rate: { width: [6, 2.2] },
    duration: { width: [6, 1.8] },
    depositGuarantee: { width: [3, 1.8], visibility: ['none', 'block'] },
    product: { width: [12, 4.2] },
    orderBtn: { width: [12, 2] },
  },
  [distributionChannels['de-DE']]: COMMON_BANK_CONFIGS_IN_DE_AT,
  [distributionChannels['de-AT']]: COMMON_BANK_CONFIGS_IN_DE_AT,
};

const COMMON_BANK_CONFIGS_IN_DE_AT_WITHOUT_PROMOTED_OFFER = {
  rate: { width: [4, 2.2] },
  duration: { width: [4, 2.2] },
  depositGuarantee: { width: [3, 2.2], visibility: ['none', 'block'] },
  product: { width: [4, 3.2] },
  orderBtn: { width: [12, 2.2] },
};

const BANK_OFFERS_TABLE_CONFIGS_WITHOUT_PROMOTED_OFFER = {
  default: {
    rate: { width: [4, 2.4] },
    duration: { width: [4, 2.8] },
    depositGuarantee: { width: [6, 2], visibility: ['none', 'block'] },
    product: { width: [4, 2.4] },
    orderBtn: { width: [6, 2.4] },
  },
  [distributionChannels['en-GB']]: {
    rate: { width: [6, 2] },
    duration: { width: [6, 2] },
    depositGuarantee: { width: [6, 3.6], visibility: ['none', 'block'] },
    product: { width: [6, 2.4] },
    orderBtn: { width: [6, 1.8] },
  },
  [distributionChannels['nl-NL']]: {
    rate: { width: [6, 1.8] },
    duration: { width: [6, 2.3] },
    depositGuarantee: { width: [6, 1.6], visibility: ['none', 'block'] },
    product: { width: [6, 3.7] },
    orderBtn: { width: [6, 2.4] },
  },
  [distributionChannels['pl-PL']]: {
    rate: { width: [6, 2.3] },
    duration: { width: [6, 2.6] },
    depositGuarantee: { width: [6, 1.2], visibility: ['none', 'block'] },
    product: { width: [6, 3.3] },
    orderBtn: { width: [6, 2.4] },
  },
  [distributionChannels['de-DE']]: COMMON_BANK_CONFIGS_IN_DE_AT_WITHOUT_PROMOTED_OFFER,
  [distributionChannels['de-AT']]: COMMON_BANK_CONFIGS_IN_DE_AT_WITHOUT_PROMOTED_OFFER,
};

/**
 * @param locale
 * @returns Default config or platform specific if it's defined in the table configs.
 *
 * Bank offers table configs in order to extend functionalities like spacings and visibility per markets.
 *
 * `[xs, lg] => [6, 1.8]` array items representing values per screen size.
 *
 * `visibility: ['none', 'block']` array items representing display values per screen size.
 *
 * `width: [6, 1.8]` array items representing width values per screen size.
 *
 * Values used on width should follow the Grid system.
 *
 * Example: 1 represents 8.33%, 1.8 represents 15% as value.
 */
export const getBankOffersTableConfig = (promotedProductId: string | null, locale: string) => {
  const configType = promotedProductId
    ? BANK_OFFERS_TABLE_CONFIGS
    : BANK_OFFERS_TABLE_CONFIGS_WITHOUT_PROMOTED_OFFER;

  return configType[getDistributionChannel(locale)] || configType['default'];
};

/**
 * @returns random number from 1 to 1000 to by pass the browser cache when fetch the img from Content Stack
 * Example: https://eu-images.contentstack.com/v3/assets/blt4a5ee0113ab335fb/blt55247c3b8f63812b/refer_a_friend_default.svg?v=123
 */
export const generateRandomNumber = (maxLimit = 1000) => {
  const array = new Uint32Array(1);
  window.crypto.getRandomValues(array);
  return (array[0] % maxLimit) + 1;
};

export const BANK_CHIPS_CONFIG = {
  shariaAccount: {
    key: 'shariaAccount',
    component: <ProductLabel type="ShariaAccount" />,
    tooltip: messages.chipShariaTooltip,
    excludedInChannels: [],
  },
  noForeignTax: {
    key: 'noForeignTax',
    component: <ProductLabel type="NoForeignTax" />,
    tooltip: messages.chipNoWithholdingTaxTooltip,
    excludedInChannels: [distributionChannels['en-GB'], distributionChannels['pl-PL']],
  },
  automaticTaxHandling: {
    key: 'automaticTaxHandling',
    component: <ProductLabel type="AutomaticTaxHandling" />,
    tooltip: messages.chipAutomaticTaxHandlingTooltip,
    excludedInChannels: [distributionChannels['en-GB'], distributionChannels['pl-PL']],
  },
};

export const getBankChips = (bank, distributionChannel) => {
  const chips: Array<{
    key: string;
    component: React.ReactNode;
    excludedInChannels: string[];
    tooltip: string;
  }> = [];

  if (bank?.shariah_compliant) {
    chips.push(BANK_CHIPS_CONFIG['shariaAccount']);
  }

  const {
    domestic_taxation_applicable: domesticTaxationApplicable,
    domestic_taxation: domesticTaxation,
    source_tax_applicable: sourceTaxApplicable,
    source_tax_type: sourceTaxType,
  } = bank?.branch_country || {};
  const isDomesticTaxationApplicableAvailable =
    domesticTaxationApplicable != null ? domesticTaxationApplicable : domesticTaxation;
  if (
    sourceTaxApplicable === false &&
    isDomesticTaxationApplicableAvailable === false &&
    distributionChannel !== distributionChannels['en-GB']
  ) {
    chips.push(BANK_CHIPS_CONFIG['noForeignTax']);
  }

  if (
    isDomesticTaxationApplicableAvailable === true &&
    distributionChannel !== distributionChannels['en-GB']
  ) {
    chips.push(BANK_CHIPS_CONFIG['automaticTaxHandling']);
  }

  if (
    isDomesticTaxationApplicableAvailable === false &&
    sourceTaxApplicable === true &&
    sourceTaxType === SOURCE_TAX_TYPES.domestic &&
    distributionChannel !== distributionChannels['en-GB']
  ) {
    chips.push(BANK_CHIPS_CONFIG['automaticTaxHandling']);
  }

  return chips.filter((chip) => !chip.excludedInChannels.includes(distributionChannel));
};

export const getDepositGuaranteePayoutCurrency = (
  isoCountryCode: string,
  distributionChannel: string,
): string | undefined => {
  let currency = COUNTRY_CODE_CURRENCIES[isoCountryCode];

  const countryListUsingPlatformCountryCurrencies = ['NOR', 'SWE'];

  if (countryListUsingPlatformCountryCurrencies.includes(isoCountryCode)) {
    const distributionChannelCurrencies = {
      [distributionChannels['en-GB']]: COUNTRY_CODE_CURRENCIES.GBR,
      [distributionChannels['pl-PL']]: COUNTRY_CODE_CURRENCIES.POL,
    };

    currency = distributionChannelCurrencies[distributionChannel] || COUNTRY_CODE_CURRENCIES.DEU;
  }

  return currency;
};

export const getHighestRateByAvailableProducts = (
  products: Array<DBSProduct>,
  locale: string,
): number => {
  let highestRate = 0;
  let rateField = getInterestRateType(locale);

  products.forEach((product) => {
    if (product?.interest_rates && product.interest_rates[rateField] > highestRate) {
      highestRate = product.interest_rates[rateField];
    }
  });

  return highestRate;
};

export const formatCurrencyPosition = (message: string, locale: string) => {
  if (locale === INTL_LOCALES.AUSTRIA) {
    const match = message.match(/^(\D+)(\d.*)$/);
    if (match) {
      return `${match[2]} ${match[1]}`.trim();
    }
  }
  return message;
};

export const getBankPageProductTitle = (bank, offer, intl, t) => {
  const { product_type: offerProductType } = offer;

  const productTypes = {
    [OFFER_PRODUCT_TYPES.TERM]: t(bankDetailsMessages.bankProductTypeChipTerm),
    [OFFER_PRODUCT_TYPES.NOTICE]: t(messages.productTypeNoticeAccount),
    [OFFER_PRODUCT_TYPES.OVERNIGHT]: t(messages.productTypeOvernight),
  };

  if (
    offerProductType === OFFER_PRODUCT_TYPES.NOTICE ||
    offerProductType === OFFER_PRODUCT_TYPES.OVERNIGHT
  ) {
    return `${bank.name} - ${productTypes[offerProductType]}`;
  }

  const term = formatTerm(offer, intl, t) || '';
  return `${bank.name} - ${term} - ${productTypes[offerProductType]}`;
};

export const getBankPageLink = (
  locale: string,
  shouldUseBankSlugFallback: boolean,
  bankSlug: string,
  bankName: string,
) => {
  if (!locale) return '#';

  const localePath = getDomainConfig(locale).localisedBankPages || '';
  const localeBasePath = getLocaleBasePath(locale);
  const urlDivisor = localeBasePath ? '/' : '';
  const bankSlugValue = getBankSlug(shouldUseBankSlugFallback, bankSlug, bankName);
  const bankSlugPath = bankSlugValue && bankSlugValue.replace(/^\/?/, '/');

  return `${localeBasePath}${urlDivisor}${localePath}${bankSlugPath}`.replace(/\/?$/, '/');
};

export const getBanksAPIResponse = async (fn: Function, cacheKey: string, args = {}) => {
  const isBuildModeOn = isAppDuringBuildProcess();

  let response = await Cache.get(cacheKey);

  if (!isBuildModeOn || response === null) {
    response = await fn({ ...args });
  }

  if (isBuildModeOn) {
    Cache.set(cacheKey, response, BANK_APIS_CACHE_TTL);
  }

  return response;
};

export const getBanksListPageProps = ({ defaultProps }) => defaultProps;

export const getBankDetailsProps = async ({
  locale,
  bankPaths: { bankId },
  defaultProps,
}: GetBankPropsFnParams): Promise<BankPageProps> => {
  const bankFound = defaultProps.banks?.entries?.find((bank) => {
    const bankSlug = getBankSlug(true, bank.bank_slug, bank.name);

    return bankSlug === bankId;
  });

  if (!bankFound) {
    return defaultProps;
  }

  const bankCacheKeys = getBankCacheKeys(locale, bankFound.id);

  const [bankDetailsResponse, productsResponse] = await Promise.all([
    getBanksAPIResponse(fetchBankDetails, bankCacheKeys.bankDetails, {
      bankId: bankFound.id,
      locale,
    }),
    getBanksAPIResponse(fetchPublicProducts, bankCacheKeys.bankProducts, {
      locale,
      headerId: 'getBankDetailsProps',
    }),
  ]);

  const bankOffers = productsResponse?.entries?.filter(
    (product) => product?.deposit_taking_bank?.id === bankFound.id,
  );

  return {
    ...defaultProps,
    banks: null,
    bankPage: {
      ...defaultProps.bankPage,
      bank_id: bankFound.id,
      bank_details: bankDetailsResponse,
      bank_offers: bankOffers,
      highestInterestRate: `${getHighestRateByAvailableProducts(bankOffers, locale)}`,
    },
  };
};

export const getBankProductProps = async ({
  locale,
  bankPaths,
  defaultProps,
}: GetBankPropsFnParams): Promise<BankPageProps> => {
  const bankDetailsProps = await getBankDetailsProps({ locale, bankPaths, defaultProps });

  return {
    ...bankDetailsProps,
    bankPage: {
      ...bankDetailsProps.bankPage,
      bank_offer_id: bankPaths.bankProductId,
    },
  };
};
